import * as Device from '@/platform/device';
import * as Notifications from '@/platform/notifications';
import { Platform } from '@/platform/platform';

// Configuração de como as notificações devem ser exibidas quando o app está em foreground
Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowAlert: true,
    shouldPlaySound: true,
    shouldSetBadge: true,
    shouldShowBanner: true,
    shouldShowList: true,
  }),
});

// Obtém o token FCM da plataforma. O backend tem UM só campo (firebase_token) e dispara por FCM,
// então as duas plataformas precisam devolver um token de REGISTRO FCM:
//
//  • Android: getDevicePushTokenAsync() já devolve o token FCM (via google-services.json).
//  • iOS: getDevicePushTokenAsync() devolve o token do APNs (hex cru), que o FCM REJEITA — era por
//    isso que nenhuma notificação chegava no iPhone. Quem troca o token APNs por um token FCM é o
//    SDK do Firebase Messaging, daí o @react-native-firebase/messaging só nesse caminho.
//
// O import é dinâmico de propósito: o caminho do Android continua sem carregar o módulo, mantendo
// intacto um fluxo que já estava funcionando em produção.
async function obterTokenFcm(): Promise<string | null> {
  // WEB: Platform.OS é "web" — cai no getDevicePushTokenAsync do shim, que devolve o token FCM
  // web (Firebase JS SDK + VAPID no Service Worker). O ramo iOS (Firebase nativo) não roda.
  if (Platform.OS === 'ios') {
    // Import do Firebase SDK isolado num módulo com SPLIT DE PLATAFORMA
    // (firebaseMessagingIos.ios.ts ↔ .ts): o Metro resolvia o import dinâmico
    // em tempo de bundle e, sem o pacote instalado, o BUNDLE DO ANDROID inteiro
    // quebrava ("Unable to resolve module @react-native-firebase/messaging") —
    // mesmo sendo um caminho que o Android nunca executa.
    const { obterTokenFcmViaFirebase } = await import('./firebaseMessagingIos');
    return await obterTokenFcmViaFirebase();
  }
  return (await Notifications.getDevicePushTokenAsync()).data;
}

// Busca o token FCM com algumas tentativas (backoff) — contorna o FIS_AUTH_ERROR TRANSITÓRIO do
// Firebase Installations (comum na 1ª abertura, com rede instável ou Play Services ainda
// inicializando). Retorna undefined SEM alarmar se não conseguir: o app segue funcionando sem push.
// Usa console.warn (não console.error) de propósito — console.error dispara o overlay VERMELHO de
// erro (LogBox) que o usuário via na tela.
async function fetchFcmTokenComRetry(
  tentativas = 3,
  baseDelayMs = 1500,
): Promise<string | null> {
  for (let i = 0; i < tentativas; i++) {
    try {
      const t = await obterTokenFcm();
      if (t) {
        console.log('FCM Token:', t);
        return t;
      }
    } catch (e) {
      const ultimaTentativa = i === tentativas - 1;
      if (ultimaTentativa) {
        console.warn(
          'Não foi possível obter o push token (o app segue sem notificações push):',
          String((e as any)?.message ?? e),
        );
        return null;
      }
      // Espera crescente antes de tentar de novo — o FIS costuma se autorrecuperar.
      await new Promise((r) => setTimeout(r, baseDelayMs * (i + 1)));
    }
  }
  return null;
}

export const NotificationService = {
  // Solicita permissão e obtém o token do Firebase (FCM)
  registerForPushNotificationsAsync: async (): Promise<string | null> => {
    let token: string | null = null;

    // WEB: canal de notificação é só do Android — no browser este ramo não roda.
    if (Platform.OS === 'android') {
      await Notifications.setNotificationChannelAsync('default', {
        name: 'default',
        importance: Notifications.AndroidImportance.MAX,
        vibrationPattern: [0, 250, 250, 250],
        lightColor: '#294956',
      });
    }

    if (Device.isDevice) {
      const { status: existingStatus } = await Notifications.getPermissionsAsync();
      let finalStatus = existingStatus;

      if (existingStatus !== 'granted') {
        const { status } = await Notifications.requestPermissionsAsync();
        finalStatus = status;
      }

      if (finalStatus !== 'granted') {
        console.warn('Falha ao obter permissão para notificações push!');
        return null;
      }

      // Obtém o token FCM da plataforma (ver obterTokenFcm: Android via expo-notifications, iOS via
      // Firebase Messaging), com retry/backoff. Falha NÃO é crítica (o app funciona sem push) → não
      // usar console.error (que abre o overlay de erro no app).
      token = await fetchFcmTokenComRetry();
    } else {
      console.warn('Notificações push só funcionam em dispositivos físicos.');
    }

    return token;
  },

  // Listeners para notificações
  addListener: (
    onNotification: (notification: Notifications.Notification) => void,
    onResponse: (response: Notifications.NotificationResponse) => void
  ) => {
    const notificationListener = Notifications.addNotificationReceivedListener(onNotification);
    const responseListener = Notifications.addNotificationResponseReceivedListener(onResponse);

    return () => {
      notificationListener.remove();
      responseListener.remove();
    };
  }
};
